import {
  faArrowTrendUp,
  faCreditCard,
  faBank,
  faCoins,
  faMoneyBillTransfer,
  faMoneyCheck,
  faHandHoldingHeart,
  faPiggyBank,
} from '@fortawesome/free-solid-svg-icons'
import { faApple } from '@fortawesome/free-brands-svg-icons'

// Cross-surface payment-method registry: display order, icon config,
// and labels, shared by the v7 donation widget and the Impact Hub
// fundraiser-edit checkout preview so the two can't drift apart.
//
// v6 is deliberately NOT a consumer — its tab-based chooser has its own
// vocabulary (a `paymentRequest` pseudo-method for the Stripe payment
// request tab, aria'd tab labels via paymentMethodTab) that doesn't map
// onto this registry; see donate_v6/paymentMethodLabel.js.
//
// Method keys match the backend's granular names — the values of
// PaymentWidgetConfig::FIAT_PAYMENT_METHODS' keys plus the expanded
// deferred bundle (wire/check/pledge) and the non-fiat methods.
// apple_pay / google_pay exist only client-side (the backend doesn't
// track device-wallet availability).

// Stable display order (moved here from donate_v7/paymentMethodConfig).
// AP and GP at the top (per product call: when available, they're the
// fastest path), then card, PayPal/Venmo, bank, crypto, stock, DAF, and
// pledged-payment alternatives.
export const PAYMENT_METHOD_ORDER = [
  'apple_pay', 'google_pay',
  'credit_card', 'paypal', 'venmo', 'bank',
  'crypto', 'stock', 'daf',
  'wire', 'check', 'pledge',
]

// Framework-neutral icon layer: which FontAwesome glyph represents each
// method. PayPal / Venmo / Google Pay have empty entries — they render
// pixel-perfect SVG brand marks from ./logos rather than glyphs. Apple
// Pay uses the FA brands faApple glyph (no SVG asset shipped).
const GLYPHS = {
  apple_pay:   { icon: faApple },
  google_pay:  {},
  credit_card: { icon: faCreditCard },
  bank:        { icon: faBank },
  paypal:      {},
  venmo:       {},
  crypto:      { icon: faCoins },
  stock:       { icon: faArrowTrendUp },
  daf:         { icon: faPiggyBank },
  wire:        { icon: faMoneyBillTransfer },
  check:       { icon: faMoneyCheck },
  pledge:      { icon: faHandHoldingHeart },
}

const layer = overrides => Object.fromEntries(
  Object.keys(GLYPHS).map(key => [
    key, { ...GLYPHS[key], ...(overrides[key] || {}) },
  ])
)

// Per-surface styling layers over the shared glyphs. Import ONLY the
// one for your surface — the @__PURE__ annotations let esbuild
// tree-shake the other away, so the v7 JS bundle ships no Bootstrap
// classes and the IH JS bundle none of Tailwind's. (The v7 Tailwind CSS
// build @sources this whole module, so its scanner does see the
// Bootstrap class strings — but none map to v7 utilities, so nothing
// extra is emitted; the no-Bootstrap guarantee is about the JS bundle.)
// Color tokens are the nearest equivalent in each framework, not exact
// matches; `className` carries per-method quirks (Venmo's square mark
// wants slight rounding).
export const ICONS_TAILWIND = /* @__PURE__ */ layer({
  apple_pay:   { color: 'text-black'       },
  credit_card: { color: 'text-info-500'    },
  bank:        { color: 'text-success-700' },
  venmo:       { className: 'rounded-sm'   },
  crypto:      { color: 'text-warning-600' },
  stock:       { color: 'text-danger-500'  },
  daf:         { color: 'text-danger-300'  },
  wire:        { color: 'text-warning-700' },
  check:       { color: 'text-success-700' },
  pledge:      { color: 'text-brand-500'   },
})

export const ICONS_BOOTSTRAP = /* @__PURE__ */ layer({
  apple_pay:   { color: 'text-black'            },
  credit_card: { color: 'text-info'             },
  bank:        { color: 'text-success-emphasis' },
  venmo:       { className: 'rounded-1'         },
  crypto:      { color: 'text-warning'          },
  stock:       { color: 'text-danger'           },
  daf:         { color: 'text-danger-subtle'    },
  wire:        { color: 'text-warning-emphasis' },
  check:       { color: 'text-success-emphasis' },
  pledge:      { color: 'text-primary'          },
})

// English fallbacks mirroring widgets.en.yml's payment_methods.* — used
// where there's no widget i18n instance (the Impact Hub builder is
// English-only today). Keyed by locale key, not method name (card/tbd).
const FALLBACK_LABELS = {
  card:  'Card',
  bank:  'Bank',
  crypto: 'Crypto',
  stock: 'Stock',
  wire:  'Wire',
  check: 'Check',
  tbd:   'Pledge',
}

const t = (i18n, key) =>
  i18n ? i18n.t(`payment_methods.${key}`) : FALLBACK_LABELS[key]

// Label for a payment method as shown in a chooser/preview. Brand names
// are hardcoded; everything else routes through i18n when provided
// (v7), or the English fallbacks (IH). `pledge` maps to the `tbd`
// locale key — `tbd` is the internal name (the donor's final payment
// method is genuinely To Be Decided when they follow the email link),
// while "Pledge" is what we currently show donors.
export const paymentMethodLabel = (method, i18n) => {
  switch (method) {
    case 'credit_card': return t(i18n, 'card')
    case 'paypal':      return 'PayPal'
    case 'venmo':       return 'Venmo'
    case 'apple_pay':   return 'Apple Pay'
    case 'google_pay':  return 'Google Pay'
    case 'daf':         return 'DAFPay'
    case 'pledge':      return t(i18n, 'tbd')
    default:            return t(i18n, method)
  }
}
